/**
 * @deprecated This page is a legacy fallback from the redirect-based PayPal flow.
 * The primary payment flow now uses embedded PayPalButtons in RegistrationModal.
 * TODO: Remove this page once the embedded flow is fully validated in production.
 * Keep for now as a webhook fallback and edge-case compatibility path.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { httpClient } from '@/api/http-client';

type Status = 'processing' | 'success' | 'error';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('processing');
  const [errorMessage, setErrorMessage] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [retryable, setRetryable] = useState(false);
  const processedRef = useRef(false);

  const token = searchParams.get('token');
  const payerId = searchParams.get('PayerID');
  const eventId = searchParams.get('eventId');

  // Read pending registration once on mount and store event_id before clearing
  const [resolvedEventId, setResolvedEventId] = useState<string | null>(eventId);
  // Keep registration_id in component state so retries work even if localStorage is lost
  const [registrationId, setRegistrationId] = useState<string | null>(null);

  // Capture logic extracted so it can be retried
  const capturePayment = useCallback(async (regId: string, providerToken: string) => {
    setStatus('processing');
    setErrorMessage('');
    try {
      await httpClient.post(
        `/registration-payment-ops/complete/${regId}`,
        { provider_order_id: providerToken },
      );

      // Only clear pendingRegistration AFTER capture succeeds
      localStorage.removeItem('pendingRegistration');
      setStatus('success');
    } catch (err: any) {
      setRetryable(true);
      setErrorMessage(err?.message || 'Payment processing failed. Please contact support.');
      setStatus('error');
    }
  }, []);

  // Process payment on mount
  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const processPayment = async () => {
      // Retrieve pending registration from localStorage with defensive parsing
      const pendingRaw = localStorage.getItem('pendingRegistration');
      let pending: { registration_id?: string; event_id?: string } | null = null;
      if (pendingRaw) {
        try {
          pending = JSON.parse(pendingRaw);
        } catch {
          // Malformed or stale data — clear it and continue without it
          localStorage.removeItem('pendingRegistration');
          pending = null;
        }
      }

      // Preserve event_id for redirect
      if (pending?.event_id && !eventId) {
        setResolvedEventId(pending.event_id);
      }

      if (!token) {
        // No payment params — just show success (direct navigation)
        localStorage.removeItem('pendingRegistration');
        setStatus('success');
        return;
      }

      if (!pending?.registration_id) {
        // We have a PayPal token but no pending registration data —
        // cannot complete payment without knowing which registration to confirm
        setErrorMessage(
          'Entry data not found. Your payment may still have been processed. ' +
          'Please check your entries or contact support.'
        );
        setStatus('error');
        return;
      }

      // Store registration_id in component state for retry resilience
      setRegistrationId(pending.registration_id);

      // Attempt capture — pendingRegistration stays in localStorage until success
      await capturePayment(pending.registration_id, token);
    };

    processPayment();
  }, [token, payerId, eventId, capturePayment]);

  // Retry handler using component-state registration_id
  const handleRetry = useCallback(() => {
    if (registrationId && token) {
      capturePayment(registrationId, token);
    }
  }, [registrationId, token, capturePayment]);

  // Auto-redirect countdown on success
  useEffect(() => {
    if (status !== 'success') return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (resolvedEventId) {
            navigate(`/events/${resolvedEventId}`);
          } else {
            navigate('/dashboard');
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status, resolvedEventId, navigate]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
          {status === 'processing' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <h2 className="text-xl font-bold">Processing Payment...</h2>
              <p className="text-sm text-muted-foreground">
                Please wait while we confirm your payment.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/10">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold">Payment Successful!</h2>
              <p className="text-sm text-muted-foreground">
                Your payment has been processed and your registration is confirmed.
              </p>
              <p className="text-xs text-muted-foreground">
                Redirecting in {countdown} seconds...
              </p>
              <div className="flex gap-3 pt-2">
                {resolvedEventId ? (
                  <Link to={`/events/${resolvedEventId}`}>
                    <Button>
                      Go to Event <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                ) : (
                  <Link to="/dashboard">
                    <Button>
                      Go to Dashboard <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                )}
                <Link to="/jackpots">
                  <Button variant="outline">Browse Jackpots</Button>
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <XCircle className="h-8 w-8 text-destructive" />
              </div>
              <h2 className="text-xl font-bold">Payment Failed</h2>
              <p className="text-sm text-muted-foreground">{errorMessage}</p>
              <div className="flex gap-3 pt-2">
                {retryable && registrationId && token && (
                  <Button onClick={handleRetry}>
                    <RefreshCw className="mr-1 h-4 w-4" /> Retry Payment
                  </Button>
                )}
                {resolvedEventId ? (
                  <Link to={`/events/${resolvedEventId}`}>
                    <Button variant={retryable ? 'outline' : 'default'}>Return to Event</Button>
                  </Link>
                ) : (
                  <Link to="/dashboard">
                    <Button variant={retryable ? 'outline' : 'default'}>Go to Dashboard</Button>
                  </Link>
                )}
                <Link to="/support">
                  <Button variant="outline">Contact Support</Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
